const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

const authStorageKey = 'vasia-dashboard-session';

const headers = (accessToken) => ({
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${accessToken || SUPABASE_ANON_KEY}`,
  'Content-Type': 'application/json'
});

const request = async (path, options = {}) => {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
  }

  const response = await fetch(`${SUPABASE_URL}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json'
    }
  });

  const text = await response.text();
  const body = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new Error(body?.msg || body?.message || body?.error_description || 'Supabase request failed.');
  }

  return body;
};

export const loadStoredSession = () => {
  try {
    const raw = localStorage.getItem(authStorageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const storeSession = (session) => {
  if (session) localStorage.setItem(authStorageKey, JSON.stringify(session));
  else localStorage.removeItem(authStorageKey);
};

export const getAuthUser = async (accessToken) => {
  return request('/auth/v1/user', {
    headers: headers(accessToken)
  });
};

export const loadSessionFromUrl = async () => {
  if (typeof window === 'undefined' || !window.location.hash) return null;

  const params = new URLSearchParams(window.location.hash.slice(1));
  const accessToken = params.get('access_token');
  if (!accessToken) return null;

  const session = {
    access_token: accessToken,
    refresh_token: params.get('refresh_token') || '',
    expires_in: Number(params.get('expires_in')) || null,
    expires_at: params.get('expires_at') || null,
    token_type: params.get('token_type') || 'bearer',
    user: null
  };

  session.user = await getAuthUser(accessToken);
  storeSession(session);
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  return session;
};

export const signInWithPassword = async (email, password) => {
  const session = await request('/auth/v1/token?grant_type=password', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
  storeSession(session);
  return session;
};

export const signUpWithInvite = async ({ email, password, name, inviteToken, redirectTo }) => {
  const query = redirectTo ? `?redirect_to=${encodeURIComponent(redirectTo)}` : '';
  const session = await request(`/auth/v1/signup${query}`, {
    method: 'POST',
    body: JSON.stringify({
      email,
      password,
      data: {
        full_name: name,
        invite_token: inviteToken
      }
    })
  });
  storeSession(session);
  return session;
};

export const signOut = async (accessToken) => {
  if (isSupabaseConfigured && accessToken) {
    await request('/auth/v1/logout', {
      method: 'POST',
      headers: headers(accessToken)
    }).catch(() => null);
  }
  storeSession(null);
};

export const getProfile = async (accessToken, userId) => {
  try {
    const rows = await request('/rest/v1/rpc/get_dashboard_profile', {
      method: 'POST',
      headers: headers(accessToken),
      body: JSON.stringify({})
    });
    if (rows?.[0]) return rows[0];
  } catch {
    // Fall back while a deployment is ahead of the latest Supabase migration.
  }

  const rows = await request(`/rest/v1/profiles?id=eq.${encodeURIComponent(userId)}&select=id,email,name,role,created_at&limit=1`, {
    headers: headers(accessToken)
  });
  return rows?.[0] || null;
};

export const acceptDashboardInvite = async ({ accessToken, inviteToken, name }) => {
  return request('/rest/v1/rpc/accept_dashboard_invite', {
    method: 'POST',
    headers: headers(accessToken),
    body: JSON.stringify({
      p_token: inviteToken,
      p_name: name || null
    })
  });
};

export const createDashboardInvite = async ({ accessToken, email, role, creatorId, expiresAt }) => {
  return request('/rest/v1/rpc/create_dashboard_invite', {
    method: 'POST',
    headers: headers(accessToken),
    body: JSON.stringify({
      p_email: email || null,
      p_role: role || 'analytics_viewer',
      p_creator_id: creatorId || null,
      p_expires_at: expiresAt || null
    })
  });
};

export const listDashboardUsers = async ({ accessToken }) => {
  return request('/rest/v1/rpc/list_dashboard_users', {
    method: 'POST',
    headers: headers(accessToken),
    body: JSON.stringify({})
  });
};

export const updateDashboardUserAccess = async ({ accessToken, userId, role, tabs }) => {
  const rows = await request('/rest/v1/rpc/update_dashboard_user_access', {
    method: 'POST',
    headers: headers(accessToken),
    body: JSON.stringify({
      p_user_id: userId,
      p_role: role,
      p_tabs: tabs
    })
  });

  return rows?.[0] || null;
};

export const deleteDashboardUser = async ({ accessToken, userId }) => {
  return request('/rest/v1/rpc/delete_dashboard_user', {
    method: 'POST',
    headers: headers(accessToken),
    body: JSON.stringify({
      p_user_id: userId
    })
  });
};
